<template>
  <div class="app-page">
    <Header ref="header" helpURL="studio/datasets/history/">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
    </Header>
    <div class="main-page-div">
      <main class="main-display-area">
        <div class="dataset-history-page">
          <div class="page-header">
            <div class="header-top">
              <div class="header-top__left">
                <IconButton
                  v-tooltip="'Back to Datasets'"
                  class="back-btn"
                  :icon="'chevron_left'"
                  :width="24"
                  :height="24"
                  @click="goBack"
                />
                <h1>{{ dataset?.name || 'Dataset History' }}</h1>
              </div>

              <Tags
                v-if="dataset?.id"
                ref="tagsRef"
                :datasetId="dataset.id"
                :currentTag="dataset?.tag || dataset?.tad || ''"
                :tagInfo="dataset"
                @action="handleTagsAction"
              />
            </div>
            <hr>
          </div>
          <div class="history-container">
            <div class="changelog-section">
              <div class="section-header">
                <h3>Change Log</h3>
              </div>
              <div class="changelog-wrapper">
                <DatasetChangeLogs
                  v-if="dataset?.id"
                  :key="changeLogsRefreshKey"
                  :datasetId="dataset.id"
                  @restored="handleTagRestored"
                  @create-tag="handleCreateTagRequested"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import Header from '@/components/Header.vue'
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue'
import IconButton from '@/components/IconButton.vue'
import DatasetChangeLogs from '@/components/DatasetComponent/DatasetManagement/DatasetChangeLogs.vue'
import Tags from '@/components/DatasetComponent/DatasetManagement/DatasetTags/Tags.vue'
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface'

const router = useRouter()
const route = useRoute()
const store = useStore()

const dataset = ref(null)
const loading = ref(true)
const changeLogsRefreshKey = ref(0)
const tagsRef = ref(null)

const datasetId = computed(() => parseInt(route.params.id))

onMounted(async () => {
  await loadDataset()
})

async function loadDataset() {
  try {
    const dsConn = new DatastoreConnect(store.state.enterpriseServerUrl)
    const response = await dsConn.api('dataset.get', { dataset_id: datasetId.value })
    if (response?.result) {
      dataset.value = response.result
    }
  } catch (error) {
    console.error('Error loading dataset:', error)
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.back()
}

async function handleTagsAction(action) {
  const actionType = typeof action === 'object' ? action?.type : action

  if (actionType === 'tag-this-version' || actionType === 'restore-tag') {
    changeLogsRefreshKey.value += 1
  }

  if (actionType === 'restore-tag' || actionType === 'tag-this-version') {
    await loadDataset()
  }
}

async function handleTagRestored() {
  changeLogsRefreshKey.value += 1
  await loadDataset()
}

function handleCreateTagRequested() {
  tagsRef.value?.openCreateTagDialog?.()
}
</script>

<style lang="scss" scoped>
.app-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.main-page-div {
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
}

.main-display-area {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}

.dataset-history-page {
  display: flex;
  flex-direction: column;
  /* Fills its flex parent (.main-display-area is the scroll container). A
     viewport height here is wrong — this panel is nested below the Header, so
     100vh/100dvh overflowed by the header height and clipped the bottom. */
  min-height: 100%;
  padding: 24px;
  background-color: white;
  overflow-y: scroll;
}

.page-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .header-top__left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  
  h1 {
    font-size: 28px;
    font-weight: 600;
    margin: 0;
  }
  
  h2 {
    font-size: 16px;
    font-weight: 400;
    margin: 0 0 12px 0;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }
  
  hr {
    width: 100%;
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 0 0 16px 0;
  }
}

.back-btn {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.history-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
}

.changelog-section {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.changelog-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}
</style>
